// Recherche par cas d'usage — moteur déterministe (sans IA), basé sur le catalogue d'écritures
// Indexe les 24 schémas d'écritures + mots-clés pour matching rapide.

import { ECRITURES } from "@/data/ecritures";

// Synonymes / variations courantes que les utilisateurs écrivent
const SYNONYMS = {
  // Verbes / actions
  vendu: ["vente", "facture", "facturé", "facturation", "céder", "cédé"],
  vente: ["vendu", "vendre", "facture", "facturation", "encaissé"],
  achete: ["achat", "acquérir", "acquis", "commande", "commandé", "facture fournisseur"],
  achat: ["achete", "acheté", "achats", "acquisition", "commande"],
  paye: ["paiement", "réglé", "règlement", "verser", "virement"],
  paiement: ["paye", "payé", "réglé", "règlement", "virement", "encaissement"],
  encaisse: ["reçu", "perçu", "règlement", "virement reçu"],
  facture: ["vendu", "vente", "achat", "facturation"],
  remboursé: ["avoir", "remboursement", "annulation", "retour"],
  avoir: ["remboursé", "remboursement", "annulation", "retour"],

  // Cycles
  marchandises: ["marchandise", "produit", "stock", "biens"],
  service: ["prestation", "consulting", "conseil", "honoraire"],
  immobilisation: ["immo", "machine", "matériel", "ordinateur", "véhicule", "équipement"],
  matériel: ["immobilisation", "machine", "équipement", "outil"],
  tva: ["taxe", "tva collectée", "tva déductible", "déclaration"],
  salaire: ["paie", "rémunération", "salariés", "personnel"],
  paie: ["salaire", "rémunération", "personnel"],
  amortissement: ["dotation", "usure", "dépréciation immo"],
  client: ["créance", "facture client", "411"],
  fournisseur: ["dette fournisseur", "facture fournisseur", "401"],
  banque: ["virement", "compte bancaire", "trésorerie"],
  loyer: ["location", "bail"],
  intérêt: ["agios", "emprunt", "financier"],

  // Termes techniques
  fnp: ["facture non parvenue", "charge à payer"],
  fae: ["facture à établir", "produit à recevoir"],
  cca: ["charge constatée d'avance", "loyer avance"],
  pca: ["produit constaté d'avance", "abonnement avance"],
  cession: ["vente immo", "céder", "vendre matériel"],
  irrecouvrable: ["perte créance", "client perdu", "liquidation"],
  douteux: ["litige", "impayé", "litigieux"],
  export: ["intracommunautaire", "ue", "étranger", "union européenne"],
  autoliquidation: ["intracommunautaire", "achat ue", "vente ue"],
};

// Tags additionnels par écriture pour améliorer le matching
const EXTRA_TAGS = {
  "achat-marchandises-tva20": ["fournisseur", "tva déductible", "stock", "approvisionnement"],
  "avoir-fournisseur": ["retour", "remboursement", "annulation achat"],
  "achat-immo-corporelle": ["immobilisation", "matériel", "machine", "ordinateur", "véhicule", "équipement"],
  "vente-marchandises-tva20": ["client", "chiffre d'affaires", "ca"],
  "vente-prestation-service": ["consulting", "honoraire", "conseil", "prestation"],
  "avoir-client": ["remboursement client", "annulation vente"],
  "vente-export-ue": ["intracommunautaire", "europe", "étranger"],
  "reglement-client-banque": ["paiement client", "encaissement", "virement client"],
  "paiement-fournisseur": ["paiement fournisseur", "règlement fournisseur"],
  "agios-bancaires": ["frais bancaires", "commission banque"],
  "paie-salaires": ["bulletin", "salarié", "embauche"],
  "charges-patronales": ["urssaf", "cotisations"],
  "reglement-paie": ["paiement salaire", "virement salaire"],
  "declaration-tva": ["ca3", "déclaration mensuelle"],
  "paiement-tva": ["règlement tva", "tva à payer"],
  "autoliquidation-tva": ["import ue", "achat étranger"],
  "amortissement-lineaire": ["dotation", "usure", "déprécier immo"],
  "cession-immo": ["vendre machine", "céder immobilisation"],
  "fnp-charges": ["facture non parvenue", "charge à payer", "clôture"],
  "fae-produits": ["facture à établir", "produit à recevoir", "clôture"],
  "cca": ["charge constatée d'avance", "loyer avance", "clôture"],
  "pca": ["produit constaté d'avance", "abonnement avance", "clôture"],
  "depreciation-client": ["litige", "impayé", "douteux", "clôture"],
  "creance-irrecouvrable": ["perte", "client perdu", "liquidation"],
};

function norm(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function expandTokens(text) {
  const t = norm(text);
  const tokens = new Set(t.split(/\s+/).filter((x) => x.length > 2));
  // Ajoute synonymes
  for (const tok of [...tokens]) {
    for (const [k, vals] of Object.entries(SYNONYMS)) {
      const nk = norm(k);
      if (tok.includes(nk) || nk.includes(tok)) {
        tokens.add(nk);
        for (const v of vals) tokens.add(norm(v));
      }
    }
  }
  return tokens;
}

export function searchEcritures(query) {
  if (!query || !query.trim()) return [];
  const tokens = expandTokens(query);
  const scored = [];

  for (const ec of ECRITURES) {
    let score = 0;
    const hay = norm(
      ec.titre + " " + ec.situation + " " + ec.cycle + " " + ec.commentaire +
      " " + (EXTRA_TAGS[ec.id] || []).join(" ") +
      " " + ec.lignes.map((l) => l.lib).join(" ")
    );
    for (const tok of tokens) {
      if (tok.length < 3) continue;
      if (hay.includes(tok)) score += 10;
    }
    // Bonus si numéro de compte cité dans la requête
    const nums = (query.match(/\b\d{2,5}\b/g) || []);
    for (const n of nums) {
      if (ec.lignes.some((l) => l.num.startsWith(n))) score += 30;
    }
    if (score > 0) scored.push({ score, ec });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 5).map((s) => s.ec);
}

// Petits exemples de questions à afficher comme starters
export const STARTERS = [
  "J'ai vendu un service de conseil 2 000 € HT",
  "J'achète un ordinateur 1 200 € TTC",
  "Mon client n'a pas payé sa facture",
  "Je dois déclarer ma TVA du mois",
  "Comment amortir une machine sur 5 ans ?",
  "Le fournisseur m'a fait un avoir",
];
